import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
/**
 * Editorial Authoring V2 — RECIPE authoring (BUILD 9).
 *
 * Rendered by EditorialSpreadWorkspaceV2.jsx in place of the generic
 * workspace body when block.master_type === "RECIPE" (integration of
 * that routing is out of scope for this file — see the BUILD 9 task).
 * Self-contained: owns its own modal chrome, save flow and save-state
 * display.
 *
 * Owner principle: «Красота в простоте». RECIPE V2 supports the full
 * culinary world — confectionery AND bread/sourdough AND savoury
 * cooking (marinades, sauces, meat, etc.) — never only confectionery.
 * This file deliberately does NOT implement: difficulty, complexity,
 * percentages, nutrition, a cooking-time summary, separate time/
 * temperature fields, parameter chips, numbered steps, step images,
 * critical points, professional tips, or storage panels. Time and
 * temperature stay exactly where the author wrote them, inside
 * ordinary technology prose (e.g. "Выпекать примерно 15 минут при
 * 175 °C до золотистого цвета." stays exactly that — one sentence of
 * technology, not a chip).
 *
 * No import from @/components/editorial/ (the legacy authoring UI, or
 * RecipeMaster.jsx specifically). Allowed imports: @/lib/api,
 * @/lib/editorialMedia.mjs and this app's own V2 media picker. Recognition
 * comes from
 * @/lib/editorialRecipeAuthoring.mjs (structureRecipeSource) — a
 * deterministic, local, non-confectionery-locked recognizer; see that
 * file's own header for the full recognition strategy. No provider
 * call anywhere in this authoring flow. Provider calls = 0, AI cost = $0.
 *
 * ── STORAGE CONTRACT ──────────────────────────────────────────────────
 *   content.recipe (NEW V2 fields, merged into whatever already exists
 *   there — legacy fields are never dropped, see LEGACY SAFETY below):
 *     source_text, recognized_source_text, eyebrow, title, subtitle,
 *     yield, hero, components, unresolved_lines
 *   component:  { id, title, ingredients, technology }
 *   ingredient: { id, name, qty, unit, note }
 *   hero:       { media_id, url, alt, focal_x, focal_y, zoom }
 * `technology` is always ONE connected editable text string per
 * component — never an array of numbered steps. A component may
 * validly be ingredients+technology, ingredients-only, or
 * technology-only; a truly single-component recipe with no detected
 * heading gets one component with an empty title — never fabricated.
 *
 * ── SOURCE-FIRST FLOW + STALE SAFETY ─────────────────────────────────
 * Identical contract to the already-accepted HERO / EDITORIAL_STORY /
 * PROCESS_STORY pattern: recognizedSourceText is a snapshot of the
 * source that produced the current title/subtitle/yield/components.
 * sourceText diverging from that snapshot marks the result STALE
 * ("Текст изменён — распознайте рецепт заново.") and disables Save
 * until the Owner re-recognizes. Opening the source editor alone never
 * mutates or clears anything.
 *
 * ── LEGACY SAFETY (§12–13 of the BUILD 9 task) ───────────────────────
 * If content.recipe.components already exists, it is used as-is. If it
 * doesn't, but legacy content.recipe.ingredient_groups exists (the pre-
 * V2 shape: { id, title, rows: [{id,name,qty,unit,note}] }), a local V2
 * presentation is DERIVED from it on open (never written back merely
 * by opening): each group becomes a component with its rows mapped 1:1
 * to the new ingredient shape. Legacy content.recipe.technology_steps
 * (pre-V2: { id, title, instruction, ... }) are matched onto those
 * derived components by exact title match (or, when there is only one
 * component, all steps land on it — unambiguous by construction); any
 * step that still can't be matched unambiguously is NOT silently
 * dropped — its instruction text is kept and surfaced as a calm review
 * notice (see legacyUnmatchedTechnology below) so the Owner can move it
 * manually. source_text is never fabricated from this derived data —
 * a legacy block with no source_text shows its structured result
 * immediately (via the same hasLegacyStructuredResult pattern already
 * accepted for PROCESS_STORY) with a prominent, explicit
 * "Вставить исходный текст рецепта" entry point into the new flow,
 * without ever discarding the existing structure just by opening it.
 * On Save, `...(c.recipe || {})` is spread first so any other unknown/
 * legacy key (mould, critical_points, professional_tips, storage_items,
 * original_text, etc.) is preserved untouched — only the new V2 keys
 * are overwritten.
 *
 * ── MAIN PHOTO ────────────────────────────────────────────────────────
 * One optional photo. RECIPE V2 deliberately keeps the publication image
 * at its natural ratio: no forced 4:5 frame and no crop-specific UI in
 * this first simple contract. Existing focal/zoom values are preserved in
 * storage for compatibility but are not allowed to impose a synthetic
 * aspect ratio on the Reader. Publication image has square corners; the
 * gallery thumbnail is integrated separately from content.recipe.hero.url.
 *
 * ── READER-STYLE PREVIEW ──────────────────────────────────────────────
 * One ordered, single-column editorial reading flow for BOTH Desktop
 * and Mobile — RECIPE does not use a photo-left/text-right split the
 * way other V2 masters do; component order always matches source
 * order, never rebalanced into columns or masonry. No cards, no
 * table-like technical UI. No confirmed Reader color palette was given
 * for this master (unlike HERO/EDITORIAL_STORY/MACRO_SENSORY/
 * PORTRAIT_DIALOGUE/PROCESS_STORY, which each got exact hex values) —
 * flagged as an assumption: this preview uses the same neutral
 * espresso/cream admin tokens as the rest of this authoring UI rather
 * than guessing an unconfirmed Reader palette.
 *
 * Provider calls = 0. AI cost = $0.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";
import { structureRecipeSource } from "@/lib/editorialRecipeAuthoring.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

function compact(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

let idSeq = 0;
function newId(prefix) {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

function normalizeIngredient(raw, i) {
  return {
    id: raw?.id || newId(`ing-${i}`),
    name: raw?.name || "",
    qty: raw?.qty != null ? String(raw.qty) : "",
    unit: raw?.unit || "",
    note: raw?.note || "",
  };
}

function normalizeComponent(raw, i) {
  return {
    id: raw?.id || newId(`comp-${i}`),
    title: raw?.title || "",
    ingredients: Array.isArray(raw?.ingredients) ? raw.ingredients.map(normalizeIngredient) : [],
    technology: raw?.technology || "",
  };
}

function normalizeHero(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

function sectionKey(value) {
  return compact(value).replace(/[«»"']/g, "").toLowerCase();
}

// §13 legacy mapping — see file header. Never called on Save, only to
// derive an initial local presentation from a pre-V2 recipe.
function deriveLegacyComponents(recipe) {
  const groups = Array.isArray(recipe.ingredient_groups) ? recipe.ingredient_groups : [];
  const steps = Array.isArray(recipe.technology_steps) ? recipe.technology_steps : [];

  const components = groups.map((g, i) => ({
    id: g?.id || newId(`legacy-comp-${i}`),
    title: g?.title || "",
    ingredients: Array.isArray(g?.rows)
      ? g.rows.map((r, j) => ({
        id: r?.id || newId(`legacy-ing-${i}-${j}`),
        name: r?.name || "",
        qty: r?.qty != null ? String(r.qty) : "",
        unit: r?.unit || "",
        note: r?.note || "",
      }))
      : [],
    technology: "",
  }));

  const unmatched = [];
  for (const step of steps) {
    const text = compact(step?.instruction);
    if (!text) continue;
    const key = sectionKey(step?.title);
    const match = key ? components.find((c) => sectionKey(c.title) === key) : null;
    if (match) {
      match.technology = match.technology ? `${match.technology}\n\n${text}` : text;
    } else if (components.length === 1) {
      components[0].technology = components[0].technology ? `${components[0].technology}\n\n${text}` : text;
    } else {
      unmatched.push(text);
    }
  }

  return { components, unmatched };
}

function deriveInitial(content) {
  const c = content || {};
  const r = c.recipe || {};
  const sourceText = String(r.source_text || "");
  const recognizedSourceText = String(r.recognized_source_text ?? sourceText);

  let components = [];
  let legacyUnmatchedTechnology = [];

  if (Array.isArray(r.components)) {
    components = r.components.map(normalizeComponent);
  } else if (Array.isArray(r.ingredient_groups) && r.ingredient_groups.length) {
    const derived = deriveLegacyComponents(r);
    components = derived.components;
    legacyUnmatchedTechnology = derived.unmatched;
  }

  return {
    sourceText,
    recognizedSourceText,
    eyebrow: r.eyebrow || "",
    title: r.title || "",
    subtitle: r.subtitle || "",
    yieldText: r.yield || "",
    components,
    unresolvedLines: Array.isArray(r.unresolved_lines) ? r.unresolved_lines : [],
    legacyUnmatchedTechnology,
    hero: normalizeHero(r.hero),
  };
}

function RecipeIngredientLine({ ingredient }) {
  const qtyPart = [ingredient.qty, ingredient.unit].filter(Boolean).join(" ");
  return (
    <div className="text-sm text-espresso/80">
      {ingredient.name}
      {qtyPart && <span className="text-espresso/50"> — {qtyPart}</span>}
      {ingredient.note && <span className="text-espresso/40"> ({ingredient.note})</span>}
    </div>
  );
}

function RecipeComponentPreview({ component }) {
  const hasContent = component.title || component.ingredients.length || component.technology;
  if (!hasContent) return null;
  return (
    <div className="mt-6 first:mt-0">
      {component.title && (
        <div className="font-serif text-lg text-espresso">{component.title}</div>
      )}
      {component.ingredients.length > 0 && (
        <div className={`space-y-1 ${component.title ? "mt-2" : ""}`}>
          {component.ingredients.map((ing) => <RecipeIngredientLine key={ing.id} ingredient={ing} />)}
        </div>
      )}
      {component.technology && (
        <div className={`whitespace-pre-wrap text-sm leading-relaxed text-espresso/75 ${component.title || component.ingredients.length ? "mt-2" : ""}`}>
          {component.technology}
        </div>
      )}
    </div>
  );
}

function RecipeImage({ image, maxWidthClass }) {
  const resolvedUrl = useEditorialMediaUrl(image?.url);
  if (!image?.url) return null;
  return (
    <div className={`relative mx-auto aspect-[4/5] overflow-hidden bg-espresso/10 ${maxWidthClass}`}>
      <img
        src={resolvedUrl}
        alt={image.alt || ""}
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          objectPosition: `${(image.focal_x ?? 0.5) * 100}% ${(image.focal_y ?? 0.5) * 100}%`,
          transform: `scale(${image.zoom ?? 1})`,
          transformOrigin: `${(image.focal_x ?? 0.5) * 100}% ${(image.focal_y ?? 0.5) * 100}%`,
        }}
      />
    </div>
  );
}

function RecipePreview({ eyebrow, title, subtitle, yieldText, components, hero, maxWidthClass }) {
  return (
    <div className={`mx-auto text-center ${maxWidthClass}`}>
      <RecipeImage image={hero} maxWidthClass="w-full" />
      <div className="mt-4">
        {eyebrow && <div className="text-[10px] uppercase tracking-widest text-espresso/50">{eyebrow}</div>}
        {title && <div className="mt-1 font-serif text-2xl leading-snug text-espresso">{title}</div>}
        {subtitle && <div className="mt-2 font-serif text-sm italic text-espresso/70">{subtitle}</div>}
        {yieldText && <div className="mt-1 text-xs text-espresso/50">{yieldText}</div>}
      </div>
      <div className="mt-6 text-left">
        {components.map((comp) => <RecipeComponentPreview key={comp.id} component={comp} />)}
      </div>
    </div>
  );
}

export function EditorialRecipeV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);

  const [sourceText, setSourceText] = useState(initial.sourceText);
  const [recognizedSourceText, setRecognizedSourceText] = useState(initial.recognizedSourceText);
  const [sourceEditing, setSourceEditing] = useState(false);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [title, setTitle] = useState(initial.title);
  const [subtitle, setSubtitle] = useState(initial.subtitle);
  const [yieldText, setYieldText] = useState(initial.yieldText);
  const [components, setComponents] = useState(initial.components);
  const [unresolvedLines, setUnresolvedLines] = useState(initial.unresolvedLines);
  const [legacyUnmatchedTechnology] = useState(initial.legacyUnmatchedTechnology);

  const [hero, setHero] = useState(initial.hero);
  const displayHeroUrl = useEditorialMediaUrl(hero?.url);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const fileInputRef = useRef(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setSourceText(next.sourceText);
    setRecognizedSourceText(next.recognizedSourceText);
    setSourceEditing(false);
    setEyebrow(next.eyebrow);
    setTitle(next.title);
    setSubtitle(next.subtitle);
    setYieldText(next.yieldText);
    setComponents(next.components);
    setUnresolvedLines(next.unresolvedLines);
    setHero(next.hero);
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);
  const hasStructuredResult = components.length > 0 || Boolean(title.trim());
  const isStale = sourceText.trim() !== recognizedSourceText.trim();
  const sourceNeedsRecognition = Boolean(sourceText.trim()) && isStale;
  const hasLegacyStructuredResult = hasStructuredResult && !sourceText.trim();

  // ── Recognition ──────────────────────────────────────────────────
  const recognize = () => {
    const result = structureRecipeSource(sourceText);
    setTitle(result.title || "");
    setSubtitle(result.subtitle || "");
    setYieldText(result.yield || "");
    setComponents(result.components.map(normalizeComponent));
    setUnresolvedLines(result.unresolved_lines);
    setRecognizedSourceText(sourceText);
    setSourceEditing(false);
  };

  const clearRecognizedResult = () => {
    setRecognizedSourceText("");
    setTitle("");
    setSubtitle("");
    setYieldText("");
    setComponents([]);
    setUnresolvedLines([]);
  };

  const handleSourceTextChange = (value) => {
    setSourceText(value);
    if (!value.trim()) clearRecognizedResult();
  };
  const returnToRecognized = () => setSourceEditing(false);

  // ── Component / ingredient editing — secondary correction controls ──
  const addComponent = () => setComponents((rows) => [...rows, { id: newId("comp"), title: "", ingredients: [], technology: "" }]);
  const updateComponentField = (index, field, value) => {
    setComponents((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };
  const removeComponent = (index) => setComponents((rows) => rows.filter((_, i) => i !== index));
  const moveComponent = (index, dir) => {
    setComponents((rows) => {
      const to = index + dir;
      if (to < 0 || to >= rows.length) return rows;
      const next = [...rows];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  const addIngredient = (componentIndex) => {
    setComponents((rows) => rows.map((r, i) => (
      i === componentIndex ? { ...r, ingredients: [...r.ingredients, { id: newId("ing"), name: "", qty: "", unit: "", note: "" }] } : r
    )));
  };
  const updateIngredient = (componentIndex, ingredientIndex, field, value) => {
    setComponents((rows) => rows.map((r, i) => (
      i === componentIndex
        ? { ...r, ingredients: r.ingredients.map((ing, j) => (j === ingredientIndex ? { ...ing, [field]: value } : ing)) }
        : r
    )));
  };
  const removeIngredient = (componentIndex, ingredientIndex) => {
    setComponents((rows) => rows.map((r, i) => (
      i === componentIndex ? { ...r, ingredients: r.ingredients.filter((_, j) => j !== ingredientIndex) } : r
    )));
  };

  // ── Main photo ───────────────────────────────────────────────────
  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    setHero(normalizeHero({ media_id: media.id, url }));
    setLibraryOpen(false);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setHero(normalizeHero({ media_id: data.id, url }));
      toast.success("Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    setHero((img) => (img ? { ...img, focal_x, focal_y, zoom } : img));
    setCropOpen(false);
  };

  const clearRecipe = async () => {
    if (!window.confirm("Очистить весь рецепт, включая фото и распознанные данные?")) return;
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        recipe: {
          ...(c.recipe || {}),
          source_text: "",
          recognized_source_text: "",
          eyebrow,
          title: "",
          subtitle: "",
          yield: "",
          hero: null,
          components: [],
          unresolved_lines: [],
        },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSourceText("");
      setRecognizedSourceText("");
      setSourceEditing(false);
      setTitle("");
      setSubtitle("");
      setYieldText("");
      setComponents([]);
      setUnresolvedLines([]);
      setHero(null);
      setShowPreview(false);
      setSaveState("saved");
      onSaved?.({ ...block, content: nextContent }, []);
      toast.success("Рецепт очищен");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  // ── Save ─────────────────────────────────────────────────────────
  const save = async () => {
    if (sourceNeedsRecognition) return;
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        recipe: {
          ...(c.recipe || {}), // preserve unknown/legacy fields untouched
          source_text: sourceText,
          recognized_source_text: recognizedSourceText,
          eyebrow,
          title,
          subtitle,
          yield: yieldText,
          hero: hero ? { ...hero } : null,
          components: components.map((comp) => ({
            id: comp.id,
            title: comp.title,
            technology: comp.technology,
            ingredients: comp.ingredients.map((ing) => ({ id: ing.id, name: ing.name, qty: ing.qty, unit: ing.unit, note: ing.note })),
          })),
          unresolved_lines: unresolvedLines,
        },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = hero?.media_id && hero?.url ? [{ id: hero.media_id, url: hero.url }] : [];
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  const workspaceTitle = title || "Рецепт";
  const previewMaxWidth = previewDevice === "mobile" ? "max-w-[240px]" : "max-w-lg";

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-recipe-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Рецепт</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{workspaceTitle}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-recipe-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          {/* Main photo */}
          <div>
            <label className="mb-1 block text-[11px] text-espresso/65">Основное фото · необязательно</label>
            {hero?.url ? (
              <div className="flex items-start gap-3">
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
                  <img src={displayHeroUrl || ""} alt={hero.alt || ""} className="h-full w-full object-cover"
                    style={{ objectPosition: `${hero.focal_x * 100}% ${hero.focal_y * 100}%` }} />
                </div>
                <div className="flex-1 space-y-1.5">
                  <input value={hero.alt} onChange={(e) => setHero((img) => (img ? { ...img, alt: e.target.value } : img))}
                    placeholder="Описание изображения (alt)"
                    className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
                    data-testid="editorial-v2-recipe-hero-alt" />
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50"
                      data-testid="editorial-v2-recipe-hero-replace">
                      {uploading ? "Загрузка…" : "Заменить фото"}
                    </button>
                    <button type="button" onClick={() => setLibraryOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                      data-testid="editorial-v2-recipe-hero-library">
                      Из Медиатеки
                    </button>
                    <button type="button" onClick={() => setCropOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                      data-testid="editorial-v2-recipe-hero-crop">
                      Настроить кадр
                    </button>
                    <button type="button" onClick={() => setHero(null)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                      data-testid="editorial-v2-recipe-hero-remove">
                      Удалить фото
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                  data-testid="editorial-v2-recipe-hero-upload">
                  {uploading ? "Загрузка…" : "Загрузить фото"}
                </button>
                <button type="button" onClick={() => setLibraryOpen(true)}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                  data-testid="editorial-v2-recipe-hero-library-empty">
                  Из Медиатеки
                </button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
              onChange={handleFileSelected} data-testid="editorial-v2-recipe-hero-file-input" />
          </div>

          {/* Manual, recognition-independent field */}
          <div>
            <label className="mb-1 block text-[11px] text-espresso/60">Метка раздела</label>
            <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
              data-testid="editorial-v2-recipe-eyebrow" />
          </div>

          <div className="rounded-2xl border border-espresso/10 bg-white/70 px-4 py-4 space-y-3">
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Название рецепта</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Например: Мягкие булочки"
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-recipe-title-primary" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Краткое описание · необязательно</label>
              <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-recipe-subtitle-primary" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Выход · необязательно</label>
              <input value={yieldText} onChange={(e) => setYieldText(e.target.value)}
                placeholder="Например: 15 шт."
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-recipe-yield-primary" />
            </div>
          </div>

          {/* Legacy entry point */}
          {hasLegacyStructuredResult && !sourceEditing && (
            <div className="rounded-2xl border border-espresso/20 bg-white px-4 py-4">
              <div className="text-sm font-medium text-espresso">Новый способ подготовки текста</div>
              <p className="mt-1 text-xs text-espresso/60">
                Вставьте рецепт целиком — Редакция распознает компоненты, ингредиенты и технологию.
                Текущий результат сохранится, пока вы не запустите распознавание заново.
              </p>
              <button type="button" onClick={() => setSourceEditing(true)}
                className="mt-3 rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream"
                data-testid="editorial-v2-recipe-legacy-entry">
                Вставить исходный текст рецепта
              </button>
            </div>
          )}

          {legacyUnmatchedTechnology.length > 0 && (
            <div className="rounded-xl border border-espresso/15 bg-cream/60 px-4 py-3 text-xs text-espresso/70">
              <div className="font-medium text-espresso">
                Часть текста из старого рецепта не удалось однозначно привязать к компоненту
              </div>
              <div className="mt-2 space-y-2">
                {legacyUnmatchedTechnology.map((t, i) => <p key={i} className="whitespace-pre-wrap">{t}</p>)}
              </div>
              <p className="mt-2 text-espresso/50">Перенесите этот текст в нужный компонент вручную при необходимости.</p>
            </div>
          )}

          {/* Source-first recognition flow */}
          {(!hasStructuredResult || sourceEditing) && (
            <div>
              <label className="mb-1 block text-[11px] text-espresso/65">Исходный текст рецепта</label>
              <textarea
                value={sourceText}
                onChange={(e) => handleSourceTextChange(e.target.value)}
                rows={12}
                placeholder={"Вставьте рецепт целиком.\nРедакция выделит название, компоненты, ингредиенты и технологию."}
                className="w-full rounded-xl border border-espresso/20 bg-white px-3 py-3 text-sm leading-relaxed outline-none focus:border-espresso/45"
                data-testid="editorial-v2-recipe-source"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={recognize} disabled={!sourceText.trim()}
                  className="rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream disabled:opacity-40"
                  data-testid="editorial-v2-recipe-recognize">
                  Распознать рецепт
                </button>
                {hasStructuredResult && !isStale && (
                  <button type="button" onClick={returnToRecognized}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-recipe-return-to-recognized">
                    Вернуться к подготовленному тексту
                  </button>
                )}
              </div>
              {hasStructuredResult && isStale && (
                <p className="mt-2 text-xs text-espresso/60" data-testid="editorial-v2-recipe-stale">
                  Текст изменён — распознайте рецепт заново.
                </p>
              )}
            </div>
          )}

          {/* Structured result — the primary reviewed artifact */}
          {hasStructuredResult && (
            <div className="rounded-2xl border border-espresso/15 bg-white/70 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-espresso">
                  <span aria-hidden="true">✓</span>
                  <span>Результат распознавания</span>
                </div>
                {!hasLegacyStructuredResult && (
                  <button type="button" onClick={() => setSourceEditing(true)}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-recipe-edit-source">
                    Изменить исходный текст
                  </button>
                )}
              </div>

              <div className="mt-3 space-y-3">
                <div>
                  <div className="mb-1 text-[11px] text-espresso/60">Компоненты</div>
                  <div className="space-y-3">
                    {components.map((comp, ci) => (
                      <div key={comp.id} className="rounded-lg border border-espresso/10 p-2">
                        <input value={comp.title} onChange={(e) => updateComponentField(ci, "title", e.target.value)}
                          placeholder="Название компонента · необязательно"
                          className="w-full rounded-lg border border-espresso/15 px-2 py-1 text-xs"
                          data-testid={`editorial-v2-recipe-component-title-${ci}`} />

                        <div className="mt-2 space-y-1.5">
                          {comp.ingredients.map((ing, ii) => (
                            <div key={ing.id} className="flex flex-wrap gap-1.5">
                              <input value={ing.name} onChange={(e) => updateIngredient(ci, ii, "name", e.target.value)}
                                placeholder="Ингредиент"
                                className="min-w-0 flex-1 rounded border border-espresso/15 px-2 py-1 text-xs"
                                data-testid={`editorial-v2-recipe-ing-name-${ci}-${ii}`} />
                              <input value={ing.qty} onChange={(e) => updateIngredient(ci, ii, "qty", e.target.value)}
                                placeholder="Кол-во" className="w-16 rounded border border-espresso/15 px-2 py-1 text-xs"
                                data-testid={`editorial-v2-recipe-ing-qty-${ci}-${ii}`} />
                              <input value={ing.unit} onChange={(e) => updateIngredient(ci, ii, "unit", e.target.value)}
                                placeholder="Ед." className="w-14 rounded border border-espresso/15 px-2 py-1 text-xs"
                                data-testid={`editorial-v2-recipe-ing-unit-${ci}-${ii}`} />
                              <button type="button" onClick={() => removeIngredient(ci, ii)}
                                className="text-[10px] text-espresso/45 underline underline-offset-2"
                                data-testid={`editorial-v2-recipe-ing-remove-${ci}-${ii}`}>
                                удалить
                              </button>
                            </div>
                          ))}
                        </div>
                        <button type="button" onClick={() => addIngredient(ci)}
                          className="mt-1.5 text-[10px] text-espresso/45 underline underline-offset-2"
                          data-testid={`editorial-v2-recipe-ing-add-${ci}`}>
                          + добавить ингредиент
                        </button>

                        <textarea value={comp.technology} onChange={(e) => updateComponentField(ci, "technology", e.target.value)}
                          rows={3} placeholder="Технология"
                          className="mt-2 w-full rounded-lg border border-espresso/15 px-2 py-1 text-xs"
                          data-testid={`editorial-v2-recipe-component-technology-${ci}`} />

                        <div className="mt-1.5 flex gap-3 text-[10px] text-espresso/45">
                          <button type="button" onClick={() => moveComponent(ci, -1)} disabled={ci === 0}
                            className="underline underline-offset-2 disabled:no-underline disabled:opacity-40"
                            data-testid={`editorial-v2-recipe-component-up-${ci}`}>
                            вверх
                          </button>
                          <button type="button" onClick={() => moveComponent(ci, 1)} disabled={ci === components.length - 1}
                            className="underline underline-offset-2 disabled:no-underline disabled:opacity-40"
                            data-testid={`editorial-v2-recipe-component-down-${ci}`}>
                            вниз
                          </button>
                          <button type="button" onClick={() => removeComponent(ci)}
                            className="underline underline-offset-2"
                            data-testid={`editorial-v2-recipe-component-remove-${ci}`}>
                            удалить компонент
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addComponent}
                    className="mt-2 text-[11px] text-espresso/45 underline underline-offset-2"
                    data-testid="editorial-v2-recipe-component-add">
                    + добавить компонент вручную
                  </button>
                </div>
              </div>
            </div>
          )}

          {unresolvedLines.length > 0 && (
            <div className="rounded-xl border border-espresso/15 bg-cream/60 px-4 py-3 text-xs text-espresso/70">
              <div className="font-medium text-espresso">Не удалось однозначно распознать</div>
              <div className="mt-2 space-y-1">
                {unresolvedLines.map((l, i) => <p key={i}>{l}</p>)}
              </div>
            </div>
          )}

          {/* Local preview — single ordered editorial column, Desktop/Mobile scale only */}
          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-recipe-preview">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "desktop" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-recipe-preview-desktop">
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "mobile" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-recipe-preview-mobile">
                    Mobile
                  </button>
                </div>
              </div>
              <RecipePreview eyebrow={eyebrow} title={title} subtitle={subtitle} yieldText={yieldText}
                components={components} hero={hero} maxWidthClass={previewMaxWidth} />
            </div>
          )}
        </div>

        {sourceNeedsRecognition && (
          <div className="border-t border-espresso/10 bg-cream/70 px-5 py-2 text-center text-xs text-espresso/60"
            data-testid="editorial-v2-recipe-save-guard">
            Текст изменён — распознайте рецепт заново.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={clearRecipe}
            className="mr-auto rounded-xl border border-red-900/15 bg-white px-4 py-2 text-sm text-red-800"
            data-testid="editorial-v2-recipe-clear">
            Очистить рецепт
          </button>
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-recipe-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-recipe-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving" || sourceNeedsRecognition}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
            data-testid="editorial-v2-recipe-save">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={libraryOpen} onClose={() => setLibraryOpen(false)} onPick={pickFromLibrary} />
      <EditorialCropV2
        open={cropOpen}
        url={displayHeroUrl}
        aspectRatio="4/5"
        frameLabel="Фото рецепта · 4:5"
        focalX={hero?.focal_x ?? 0.5}
        focalY={hero?.focal_y ?? 0.5}
        zoom={hero?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropOpen(false)}
      />

    </div>
  );
}
// Pure read-only reuse of the accepted V2 recipe preview. No editor is mounted.
export function EditorialRecipeReaderV2({ block, mode = "desktop" }) {
  const data = deriveInitial(block?.content);
  return <section data-testid={`book-master-recipe-${mode}`}>
    <RecipePreview {...data} maxWidthClass={mode === "mobile" ? "max-w-[390px]" : "max-w-[860px]"} />
    {data.legacyUnmatchedTechnology.map((text, i) => <p key={i} className="mt-5 whitespace-pre-wrap">{text}</p>)}
  </section>;
}
